import React, { useState } from "react";
import { Field } from "formik";
import Popup from "../popup/Popup";
import { POPUP_FORM_CONTENT_WIDTH_CLASS, ACTION_POPUP_CONTENT_INNER_CLASS } from "../popup/popupLayoutSpec";
import Title from "../common/Title/Title";
import { AccountPopupSaveFooter, AccountPopupUploadArea, AccountPopupUploadPlaceholder } from "./accountPopupFields";
import {
  AdminPageTitle,
  AdminDetailFieldLabel,
  AdminSupportingText,
  AdminFormFieldError,
} from "../../sections/AdminDashboard/shared/adminUx";
import ImageCropper from "../common/ImageCropper/ImageCropper";
import Modal from "../common/Modal/Modal";
import Spinner from "../common/Spinner";
import Alerts from "../common/Alerts/Alerts";
import { validateFile, getAcceptAttribute } from "../../utils/contentSafety";
import { FileUploadQuery } from "../../services/FileUploadService";
import { useGraphQL } from "../../context/GraphQLProvider";
import { useDispatch } from "react-redux";
import { setFile, clearFile } from "../../Redux/Slice/FileUpload";

const MediaUploadPopup = ({ isOpen, onClose, errors, initialStep = 0, onCompletionUpdate }) => {
  const graphqlService = useGraphQL();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [coverImages, setCoverImages] = useState({
    main: null,
    overlay1: null,
    overlay2: null,
    overlay3: null
  });
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editingCoverImage, setEditingCoverImage] = useState(null);
  const [profilePictureUploaded, setProfilePictureUploaded] = useState(null);

  const steps = [
    {
      title: "Profile Picture Upload",
      description: "Upload your profile picture (JPEG or PNG format)",
      field: "profilePicture",
      type: "profile",
      accept: getAcceptAttribute("image"),
      required: false
    },
    {
      title: "Cover Images",
      description: "Upload and arrange your cover images",
      type: "coverImages",
      required: false
    }
  ];

  const handleNext = () => {
    // Save cover images to form values when user clicks save
    if (currentStepData.type === "coverImages") {
      // Check if any cover images have been uploaded
      const hasAnyCoverImage = coverImages.main?.imagePreview || 
                              coverImages.overlay1?.imagePreview || 
                              coverImages.overlay2?.imagePreview || 
                              coverImages.overlay3?.imagePreview;
      
      if (hasAnyCoverImage) {
        // Notify parent component about completion
        if (onCompletionUpdate) {
          onCompletionUpdate("coverImageUpload", "completed");
        }
      }
    }
    onClose();
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFileInputChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const result = validateFile(file, "image");
      if (!result.valid) {
        Alerts.error(result.message);
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
      await handleDirectUpload(file);
    }
    e.target.value = "";
  };

  const handleModalClose = () => {
    setShowCropper(false);
    setSelectedFile(null);
    setEditingCoverImage(null);
    dispatch(clearFile());
  };

  const handleDirectUpload = async (file) => {
    console.log("Direct upload called with file:", file);
    setIsUploading(true);
    try {
      // Upload the file directly without cropping
      const uploadResponse = await FileUploadQuery(graphqlService, { file: file });
      
      if (uploadResponse?.uploadFile?.success) {
        const imageUrl = uploadResponse.uploadFile.url;
        console.log("Upload successful, new URL:", imageUrl);
        
        if (editingCoverImage) {
          // Handle cover image upload
          setCoverImages(prev => ({
            ...prev,
            [editingCoverImage]: {
              imagePreview: imageUrl,
              title: `Cover Image ${editingCoverImage}`
            }
          }));
          setEditingCoverImage(null);
        } else {
          // Handle profile picture upload
          setProfilePictureUploaded(imageUrl);
          // Notify parent component about completion
          if (onCompletionUpdate) {
            onCompletionUpdate("profilePicture", imageUrl);
          }
        }
        
        setSelectedFile(null);
        // Alerts.success("Image uploaded successfully!");
      } else {
        Alerts.error(`Upload failed: ${uploadResponse?.uploadFile?.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      Alerts.error("Error uploading image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropComplete = async (croppedImageUrl) => {
    console.log("Crop complete called with URL:", croppedImageUrl);
    setIsUploading(true);
    try {
      // Convert the object URL to a blob
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      
      // Create a File object from the blob
      const croppedFile = new File([blob], selectedFile.name, {
        type: blob.type,
        lastModified: Date.now(),
      });

      // Upload the cropped file
      const uploadResponse = await FileUploadQuery(graphqlService, { file: croppedFile });
      
      if (uploadResponse?.uploadFile?.success) {
        const imageUrl = uploadResponse.uploadFile.url;
        console.log("Upload successful, new URL:", imageUrl);
        
        if (editingCoverImage) {
          // Handle cover image upload
          setCoverImages(prev => ({
            ...prev,
            [editingCoverImage]: {
              imagePreview: imageUrl,
              title: `Cover Image ${editingCoverImage}`
            }
          }));
          setEditingCoverImage(null);
        } else {
          // Handle profile picture upload
          setProfilePictureUploaded(imageUrl);
          // Notify parent component about completion
          if (onCompletionUpdate) {
            onCompletionUpdate("profilePicture", imageUrl);
          }
        }
        
        setShowCropper(false);
        setSelectedFile(null);
        dispatch(clearFile());
        // Alerts.success("Image uploaded successfully!");
      } else {
        Alerts.error(`Upload failed: ${uploadResponse?.uploadFile?.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      Alerts.error(`Upload error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle cover image edit
  const handleCoverImageEdit = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Editing cover image type:', type);
    setEditingCoverImage(type);
    // Trigger file input
    const fileInput = document.getElementById('cover-image-input');
    if (fileInput) {
      fileInput.click();
    }
  };

  // Handle cover image file input change
  const handleCoverImageFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const result = validateFile(file, "image");
    if (!result.valid) {
      Alerts.error(result.message);
      return;
    }
    console.log('File selected:', file, 'for type:', editingCoverImage);
    // Create object URL for immediate preview
    const imageUrl = URL.createObjectURL(file);
      
      // Update the cover image directly
      setCoverImages(prev => {
        console.log('Updating cover images:', prev, 'with new image for:', editingCoverImage);
        return {
          ...prev,
          [editingCoverImage]: {
            imagePreview: imageUrl,
            file: file
          }
        };
      });
      
      setEditingCoverImage(null);
  };


  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  if (!isOpen) return null;

  return (
    <>
      {isUploading && <Spinner />}
      <Popup
        setTrigger={() => {}}
        ShowCrossButton={true}
        cross={true}
        onClose={onClose}
        businessDashboard={true}
        hideLogoInHeader={true}
      >
      <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <div className={`mx-auto hidden-scrollbar lg:pb-0 relative ${currentStepData.type === "coverImages" ? "w-[900px]" : POPUP_FORM_CONTENT_WIDTH_CLASS}`}>
          <div className={ACTION_POPUP_CONTENT_INNER_CLASS}>
            <form>
              {currentStepData.type === "profile" ? (
                // Profile Picture Upload Step
                <div className="w-full">
                  <div className="mb-4 text-center">
                    <AdminPageTitle className="text-center pb-0 text-offblack mb-10">
                      {currentStepData.title}
                    </AdminPageTitle>
                  </div>
                  <div className="flex justify-center w-full">
                    <div className="w-full mx-auto">
                      <div className="flex justify-center w-full">
                        <div className="relative size-[192px] mx-auto">
                          <div className={`size-[192px] rounded-full ${profilePictureUploaded ? "" : "border border-solid border-[#C0C0C0] border-[1.4px]"}`}>
                            {profilePictureUploaded && (
                              <img
                                className="object-center rounded-full w-full h-full cursor-pointer"
                                src={profilePictureUploaded}
                                alt="Profile"
                              />
                            )}
                            {!profilePictureUploaded && (
                              <div className="object-center rounded-full w-full h-full cursor-pointer bg-white" />
                            )}
                          </div>
                          <div className="absolute z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <div className={`flex justify-center items-center rounded-full size-[30px] pt-[2px] relative ${profilePictureUploaded && "bg-white"}`}>
                              <div>
                                <label role="button" className="flex justify-center bg-transparent">
                                  <input
                                    type="file"
                                    accept={currentStepData.accept}
                                    onChange={handleFileInputChange}
                                    style={{ display: 'none' }}
                                  />
                                  {!profilePictureUploaded ? (
                                    <Title text={"Upload"} width={"50"}>
                                      <i className="fi fi-rs-upload text-base text-offblack"></i>
                                    </Title>
                                  ) : (
                                    <Title text={"Edit"} width={"50"}>
                                      <i className="fi fi-rr-pencil text-base text-offblack"></i>
                                    </Title>
                                  )}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : currentStepData.type === "file" ? (
                <div className="flex justify-center w-full">
                  <div className="w-full mx-auto">
                    <Field name={currentStepData.field}>
                      {({ field, form }) => (
                        <div>
                          <AdminDetailFieldLabel className="mb-4 block">
                            {currentStepData.title}
                          </AdminDetailFieldLabel>
                          
                          <div className="flex items-center justify-center w-full">
                            <input
                              {...field}
                              type="file"
                              accept={currentStepData.accept}
                              onChange={handleFileInputChange}
                              className="hidden"
                              id={`${currentStepData.field}-upload`}
                            />
                            <AccountPopupUploadArea htmlFor={`${currentStepData.field}-upload`}>
                              <AccountPopupUploadPlaceholder />
                            </AccountPopupUploadArea>
                          </div>
                          
                          {profilePictureUploaded && (
                            <div className="mt-2 p-2 bg-mistygray rounded-xl border border-lightgray">
                              <AdminSupportingText className="text-offblack block">
                                Selected: Profile Picture
                              </AdminSupportingText>
                            </div>
                          )}
                          
                          
                          {form.touched[currentStepData.field] && form.errors[currentStepData.field] && (
                            <AdminFormFieldError className="mt-2">{form.errors[currentStepData.field]}</AdminFormFieldError>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
              ) : (
                // Cover Images Step
                <div className="w-full">
                  <div className="mb-4 text-center">
                    <AdminPageTitle className="text-center pb-0 text-offblack mb-10">
                      {currentStepData.title}
                    </AdminPageTitle>
                  </div>
                  {/* Cover Images with Edit Overlay */}
                  <div className="flex justify-center w-full relative -right-[18%]">
                    <div className="sm:aspect-[578/433] w-full max-w-[578px] group relative">
                      <div className="relative sm:aspect-[578/433] w-full max-w-[578px] rounded-xl overflow-visible">
                        {/* Main large image */}
                        <div className="relative w-full h-full rounded-xl overflow-hidden bg-mistygray group">
                          <img
                            src={coverImages.main?.imagePreview || ""}
                            alt="Main cover image"
                            className={`w-full h-full object-cover ${coverImages.main?.imagePreview ? 'opacity-100' : 'opacity-0'}`}
                          />
                          {/* Edit overlay on main image */}
                          <div className="absolute inset-0 bg-black opacity-0 hover:opacity-35 transition-opacity duration-300 rounded-xl"></div>
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-100 z-20">
                            <button
                              onClick={(e) => handleCoverImageEdit(e, 'main')}
                              className="flex items-center justify-center bg-white size-[48px] rounded-full cursor-pointer transition-colors duration-200 hover:opacity-100 group"
                              onMouseEnter={(e) => {
                                const overlay = e.target.closest('.group').querySelector('.absolute.inset-0.bg-black');
                                if (overlay) overlay.classList.add('opacity-35');
                              }}
                              onMouseLeave={(e) => {
                                const overlay = e.target.closest('.group').querySelector('.absolute.inset-0.bg-black');
                                if (overlay) overlay.classList.remove('opacity-35');
                              }}
                            >
                              <Title text={"Edit"} width={"40"}>
                                <i className="fi fi-rr-pencil text-base text-offblack flex items-center justify-center"></i>
                              </Title>
                            </button>
                          </div>
                        </div>
                        
                        {/* Overlay images with edit buttons */}
                        {/* Large overlay extending left */}
                        <div className="absolute -left-[44%] top-[10%] w-[280px] h-[200px] rounded-xl overflow-hidden shadow-lg z-10 group bg-mistygray">
                          <img
                            src={coverImages.overlay1?.imagePreview || ""}
                            alt="Overlay image 1"
                            className={`w-full h-full object-cover ${coverImages.overlay1?.imagePreview ? 'opacity-100' : 'opacity-0'}`}
                          />
                          <div className="absolute inset-0 bg-black opacity-0 hover:opacity-35 transition-opacity duration-300"></div>
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-100 z-20">
                            <button
                              onClick={(e) => handleCoverImageEdit(e, 'overlay1')}
                              className="flex items-center justify-center bg-white size-[48px] rounded-full cursor-pointer transition-colors duration-200"
                              onMouseEnter={(e) => {
                                const overlay = e.target.closest('.group').querySelector('.absolute.inset-0.bg-black');
                                if (overlay) overlay.classList.add('opacity-35');
                              }}
                              onMouseLeave={(e) => {
                                const overlay = e.target.closest('.group').querySelector('.absolute.inset-0.bg-black');
                                if (overlay) overlay.classList.remove('opacity-35');
                              }}
                            >
                              <Title text={"Edit"} width={"48"}>
                                <i className="fi fi-rr-pencil text-[16px] text-offblack flex items-center justify-center"></i>
                              </Title>
                            </button>
                          </div>
                        </div>
                        
                        {/* Medium overlay top right */}
                        <div className="absolute -left-[57%] top-[63%] w-[220px] h-[160px] rounded-xl overflow-hidden shadow-lg z-20 group bg-mistygray">
                          <img
                            src={coverImages.overlay2?.imagePreview || ""}
                            alt="Overlay image 2"
                            className={`w-full h-full object-cover ${coverImages.overlay2?.imagePreview ? 'opacity-100' : 'opacity-0'}`}
                          />
                          <div className="absolute inset-0 bg-black opacity-0 hover:opacity-35 transition-opacity duration-300"></div>
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-100 z-20">
                            <button
                              onClick={(e) => handleCoverImageEdit(e, 'overlay2')}
                              className="flex items-center justify-center bg-white size-[48px] rounded-full cursor-pointer transition-colors duration-200"
                              onMouseEnter={(e) => {
                                const overlay = e.target.closest('.group').querySelector('.absolute.inset-0.bg-black');
                                if (overlay) overlay.classList.add('opacity-35');
                              }}
                              onMouseLeave={(e) => {
                                const overlay = e.target.closest('.group').querySelector('.absolute.inset-0.bg-black');
                                if (overlay) overlay.classList.remove('opacity-35');
                              }}
                            >
                              <Title text={"Edit"} width={"48"}>
                                <i className="fi fi-rr-pencil text-[16px] text-offblack flex items-center justify-center"></i>
                              </Title>
                            </button>
                          </div>
                        </div>
                        
                        {/* Small overlay bottom right */}
                        <div className="absolute -left-[14%] -bottom-10 w-[180px] h-[140px] rounded-xl overflow-hidden shadow-lg z-30 group bg-mistygray">
                          <img
                            src={coverImages.overlay3?.imagePreview || ""}
                            alt="Overlay image 3"
                            className={`w-full h-full object-cover ${coverImages.overlay3?.imagePreview ? 'opacity-100' : 'opacity-0'}`}
                          />
                          <div className="absolute inset-0 bg-black opacity-0 hover:opacity-35 transition-opacity duration-300"></div>
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-100 z-20">
                            <button
                              onClick={(e) => handleCoverImageEdit(e, 'overlay3')}
                              className="flex items-center justify-center bg-white size-[48px] rounded-full cursor-pointer transition-colors duration-200"
                              onMouseEnter={(e) => {
                                const overlay = e.target.closest('.group').querySelector('.absolute.inset-0.bg-black');
                                if (overlay) overlay.classList.add('opacity-35');
                              }}
                              onMouseLeave={(e) => {
                                const overlay = e.target.closest('.group').querySelector('.absolute.inset-0.bg-black');
                                if (overlay) overlay.classList.remove('opacity-35');
                              }}
                            >
                              <Title text={"Edit"} width={"48"}>
                                <i className="fi fi-rr-pencil text-[16px] text-offblack flex items-center justify-center"></i>
                              </Title>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Hidden file input for cover images */}
                  <input
                    id="cover-image-input"
                    type="file"
                    accept={getAcceptAttribute("image")}
                    onChange={handleCoverImageFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              )}

              {/* Show save button only when profile picture is uploaded */}
              {currentStepData.type === "profile" && profilePictureUploaded && (
                <AccountPopupSaveFooter onClick={handleNext} />
              )}

              {currentStepData.type === "coverImages" &&
                (coverImages.main?.imagePreview ||
                  coverImages.overlay1?.imagePreview ||
                  coverImages.overlay2?.imagePreview ||
                  coverImages.overlay3?.imagePreview) && (
                <AccountPopupSaveFooter onClick={handleNext} />
              )}
            </form>
          </div>
        </div>
      </div>
      
    </Popup>
    
    {/* Image Cropper Modal */}
    <Modal 
      isOpen={showCropper} 
      onClose={handleModalClose}
    >
      {selectedFile && (
        <ImageCropper
          onComplete={handleCropComplete}
        />
      )}
    </Modal>
    </>
  );
};

export default MediaUploadPopup;
